import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SummarizeIcon from "@mui/icons-material/Summarize";

import API_BASE_URL from "../../config/api";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import { useSession } from "../common/useSession";
import { useViewPreferences } from "../common/useViewPreferences";
import FilterDrawer, { FilterCard, countFilled } from "../common/FilterDrawer";
import ColumnsMenu from "../common/ColumnsMenu";

const BASE_URL = API_BASE_URL;
const VIEW = "magazynNowyHandling";
const FILTERS_STORAGE_KEY = "mwNowyHandlingFilters";
const COLUMNS_STORAGE_KEY = "columnsView.magazynNowyHandling";
// Zaznaczone wiersze (wraz z danymi) trzymamy w localStorage - przetrwaja
// odswiezenie strony i nawigacje. `DOPISZ_DANE` to migawka zrobiona w chwili
// klikniecia "Dopisz dane" - kolejny etap (formularz uzupelniania) ja odczyta.
const SELECTION_STORAGE_KEY = "mwNowyHandlingSelectedRows";
const DOPISZ_DANE_STORAGE_KEY = "mwNowyHandlingDopiszDane";
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const FILTER_DEBOUNCE_MS = 250;

// Raport "Magazyn Warszawa - pelny" (kolumny z godzinami) - tylko wybrani uzytkownicy.
const FULL_REPORT_USERNAMES = ["Maciej", "plomza"];

const EMPTY_FILTERS = {
  search: "",
  nrRef: "",
  date: "",
  from: "",
  to: "",
  status: [],
  createdBy: [],
  osobaPrzygotowujacaOdprawe: [],
  kierunek: [],
  rewizja: "",
  problem: "",
  towarCelny: "",
};

// Starsza wersja zapisywala filtry pod nazwami `*Filter` i "Wszystko" jako brak filtra.
const readSavedFilters = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY));
    if (!saved || typeof saved !== "object") return EMPTY_FILTERS;
    const list = (value) => (Array.isArray(value) ? value : []);
    const yesNo = (value) => (value === "Tak" || value === "Nie" ? value : "");
    return {
      search: saved.searchText ?? "",
      nrRef: saved.nrRefFilter ?? "",
      date: saved.dateFilter ?? "",
      from: saved.dataUtworzeniaOd ?? "",
      to: saved.dataUtworzeniaDo ?? "",
      status: list(saved.statusFilter),
      createdBy: list(saved.createdByFilter),
      osobaPrzygotowujacaOdprawe: list(saved.osobaPrzygotowujacaOdpraweFilter),
      kierunek: list(saved.kierunekFilter),
      rewizja: yesNo(saved.rewizjaFilter),
      problem: yesNo(saved.problemFilter),
      towarCelny: yesNo(saved.towarCelnyFilter),
    };
  } catch (error) {
    console.error("Nie udało się wczytać filtrów:", error);
    return EMPTY_FILTERS;
  }
};

const readSavedSelection = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SELECTION_STORAGE_KEY));
    if (saved && typeof saved === "object" && !Array.isArray(saved)) return saved;
  } catch (error) {
    console.error("Nie udało się wczytać zaznaczenia:", error);
  }
  return {};
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

// -----------------------------------------------------------KOLUMNY------------
// Kolumny menu "Kolumny". Checkbox, rozwijanie historii i Nr Ref (lepkie, z lewej)
// oraz akcje (z prawej) sa zawsze widoczne.
const COLUMNS = [
  { id: "problemZPrzesylka", label: "Problem z przesyłką", width: 150 },
  { id: "statusRealizacji", label: "Status", width: 190 },
  { id: "createdBy", label: "Utworzono przez", width: 170 },
  { id: "osobaPrzygotowujacaOdprawe", label: "Osoba przygotowująca odprawę", width: 210 },
  { id: "towarCelny", label: "Towar celny", width: 110 },
  { id: "rewizja", label: "Rewizja", width: 100 },
  { id: "utworzono", label: "Data utworzenia", width: 160 },
  { id: "numerListu", label: "Nr listu", width: 160 },
  { id: "nadawca", label: "Nazwa firmy", width: 200 },
  { id: "iloscDeklPaczek", label: "Ilość dekl. paczek", width: 120 },
  { id: "iloscDeklPalet", label: "Ilość dekl. palet", width: 120 },
  { id: "iloscFaktPaczek", label: "Ilość fakt. paczek", width: 120 },
  { id: "iloscFaktPalet", label: "Ilość fakt. palet", width: 120 },
  { id: "kierunek", label: "Kierunek", width: 130 },
  { id: "serwisLotniczy", label: "Serwis lotniczy", width: 150 },
];

const DEFAULT_HIDDEN_COLUMNS = [];

// Lepkie kolumny z lewej: checkbox, rozwijanie, Nr Ref.

const STATUS_VALUES = [
  "Przyjęto na magazyn",
  "W odprawie celnej",
  "W oczekiwaniu na clenie",
  "Oclono",
  "Towar do wydania",
  "Zrealizowano",
];
const YES_NO_VALUES = ["Tak", "Nie"];

const STATUS_TONES = {
  "Przyjęto na magazyn": "blue",
  "W odprawie celnej": "amber",
  "W oczekiwaniu na clenie": "orange",
  Oclono: "teal",
  "Towar do wydania": "purple",
  Zrealizowano: "green",
};

const PILL_TONES = {
  green: { bg: "#e8f5e9", fg: "#1b5e20" },
  amber: { bg: "#fff8e1", fg: "#b26a00" },
  orange: { bg: "#fff3e0", fg: "#e65100" },
  red: { bg: "#ffebee", fg: "#c62828" },
  blue: { bg: "#e3f2fd", fg: "#1565c0" },
  teal: { bg: "#e0f2f1", fg: "#00695c" },
  purple: { bg: "#f3e5f5", fg: "#6a1b9a" },
  gray: { bg: "#eeeeee", fg: "#616161" },
};

const isYes = (value) => value === "Tak" || value === "TAK";

const matchesYesNo = (value, filter) => {
  if (filter === "Tak") return isYes(value);
  if (filter === "Nie") return !isYes(value);
  return true;
};

const parseMultiSearchValues = (value) =>
  (value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

const formatDateTime = (value) => {
  const parsed = moment.utc(value, "YYYY-MM-DDTHH:mm:ss.SSSZ");
  return parsed.isValid() ? parsed.format("DD-MM-YYYY, HH:mm") : "Brak danych";
};

const sortPl = (values) =>
  [...new Set(values.filter((v) => v && String(v).trim()))].sort((a, b) =>
    String(a).localeCompare(String(b), "pl", { sensitivity: "base" }),
  );

// W bazie przyjazd/start/koniec skanowania to sama godzina "HH:mm", a podjazd/wyjazd
// "YYYY-MM-DD HH:mm" (starsze rekordy moga miec sama godzine). Brakujaca date
// doklejamy z daty utworzenia zgloszenia. Zwraca "DD.MM.YYYY HH:mm".
const withCreatedDate = (value, utworzono) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const full = moment(text, "YYYY-MM-DD HH:mm", true);
  if (full.isValid()) return full.format("DD.MM.YYYY HH:mm");
  const time = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!time) return text;
  const created = moment.tz(utworzono, "Europe/Warsaw");
  if (!created.isValid()) return text;
  return `${created.format("DD.MM.YYYY")} ${time[1].padStart(2, "0")}:${time[2]}`;
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// Podswietlenie wiersza - pierwszenstwo (od najwyzszego): Zrealizowano >
// (Zwroty i Bledy + Rewizja razem) > Zwroty i Bledy > Rewizja. Zaznaczenie
// (checkbox) ma wlasny styl. Legenda pod tabela tlumaczy kolory.
const useRowStyles = makeStyles(() => ({
  rowDone: {
    "& > td": { backgroundColor: "#f1f8f2" },
    "&:hover > td": { backgroundColor: "#e3f1e5" },
    "& > td:first-child": { boxShadow: "inset 4px 0 0 #43a047" },
  },
  rowReturnsRevision: {
    "& > td": { backgroundColor: "#fdf0f5" },
    "&:hover > td": { backgroundColor: "#fbe1ea" },
    "& > td:first-child": { boxShadow: "inset 4px 0 0 #d81b60" },
  },
  rowReturns: {
    "& > td": { backgroundColor: "#f1f7fd" },
    "&:hover > td": { backgroundColor: "#e2eefb" },
    "& > td:first-child": { boxShadow: "inset 4px 0 0 #1e88e5" },
  },
}));

const ROW_LEGEND = [
  { key: "rowDone", label: "Status: Zrealizowano", background: "#f1f8f2", stripe: "#43a047" },
  {
    key: "rowReturnsRevision",
    label: "Numer listu w Zwroty i Błędy + Rewizja",
    background: "#fdf0f5",
    stripe: "#d81b60",
  },
  {
    key: "rowReturns",
    label: "Numer listu już w Zwroty i Błędy",
    background: "#f1f7fd",
    stripe: "#1e88e5",
  },
  { key: "rowRevision", label: "Rewizja", background: "#fffde7", stripe: COLORS.warning },
];

const getRowTint = (row, pageClasses, rowClasses) => {
  if (row.statusRealizacji === "Zrealizowano") return rowClasses.rowDone;
  const isRewizja = isYes(row.rewizja);
  const isZwrotyIBledy = Boolean(row.wZwrotyIBledy);
  if (isZwrotyIBledy && isRewizja) return rowClasses.rowReturnsRevision;
  if (isZwrotyIBledy) return rowClasses.rowReturns;
  if (isRewizja) return pageClasses.rowWarn;
  return "";
};

const describeFilters = (f) => {
  const chips = [];
  const text = (key, label) => {
    if (f[key]) chips.push({ id: key, label: `${label}: ${f[key]}`, clear: { [key]: "" } });
  };
  const list = (key, label) => {
    if (f[key].length > 0) {
      chips.push({ id: key, label: `${label}: ${f[key].join(", ")}`, clear: { [key]: [] } });
    }
  };
  text("search", "Szukaj");
  text("nrRef", "Nr ref");
  text("date", "Data");
  if (f.from || f.to) {
    chips.push({
      id: "range",
      label: `Utworzono: ${f.from || "…"} – ${f.to || "…"}`,
      clear: { from: "", to: "" },
    });
  }
  list("status", "Status");
  list("createdBy", "Utworzył");
  list("osobaPrzygotowujacaOdprawe", "Przygotowuje");
  list("kierunek", "Kierunek");
  text("rewizja", "Rewizja");
  text("problem", "Problem");
  text("towarCelny", "Towar celny");
  return chips;
};

const Pill = ({ tone, children }) => {
  const classes = usePageStyles();
  const colors = PILL_TONES[tone] || PILL_TONES.gray;
  return (
    <span className={classes.pill} style={{ background: colors.bg, color: colors.fg }}>
      {children}
    </span>
  );
};

// -----------------------------------------------------------WIERSZ-------------
const Row = React.memo(
  ({ row, columns, isSelected, onToggleSelect, onOpen, onDelete, isAdmin }) => {
    const classes = usePageStyles();
    const rowClasses = useRowStyles();
    const [open, setOpen] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [editHistory, setEditHistory] = useState([]);
    const [comments, setComments] = useState([]);

    const handleExpandClick = async () => {
      if (!open) {
        setLoadingDetails(true);
        const [history, rowComments] = await Promise.all([
          axios
            .get(`${BASE_URL}/history/${row.id}`, { headers: authHeaders() })
            .then((r) => r.data || [])
            .catch((error) => {
              console.error("Error fetching edit history:", error);
              return [];
            }),
          axios
            .get(`${BASE_URL}/comments/${row.id}`, { headers: authHeaders() })
            .then((r) => r.data || [])
            .catch((error) => {
              console.error("Error fetching comments:", error);
              return [];
            }),
        ]);
        setEditHistory(history);
        setComments(rowComments);
        setLoadingDetails(false);
      }
      setOpen((prev) => !prev);
    };

    const empty = <span className={classes.muted}>—</span>;
    const text = (value) =>
      value || value === 0 ? (
        <Tooltip title={String(value)} enterDelay={600}>
          <span className={classes.ellipsis}>{value}</span>
        </Tooltip>
      ) : (
        empty
      );

    const renderCell = (id) => {
      switch (id) {
        case "statusRealizacji":
          return row.statusRealizacji ? (
            <Pill tone={STATUS_TONES[row.statusRealizacji] || "gray"}>
              {row.statusRealizacji}
            </Pill>
          ) : (
            empty
          );
        case "problemZPrzesylka":
          return isYes(row.problemZPrzesylka) ? <Pill tone="red">Tak</Pill> : empty;
        case "rewizja":
          return isYes(row.rewizja) ? <Pill tone="amber">Tak</Pill> : empty;
        case "towarCelny":
          return isYes(row.towarCelny) ? <Pill tone="blue">Tak</Pill> : empty;
        case "utworzono":
          return formatDateTime(row.utworzono);
        default:
          return text(row[id]);
      }
    };

    return (
      <>
        <TableRow
          hover={false}
          className={`${classes.row} ${getRowTint(row, classes, rowClasses)} ${
            isSelected ? classes.rowSelected : ""
          }`}
        >
          <TableCell
            padding="checkbox"
            className={classes.stickyCell}
            style={{ left: 0, width: 52, minWidth: 52 }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={isSelected}
              onChange={() => onToggleSelect(row)}
              inputProps={{ "aria-label": `Zaznacz zgłoszenie ${row.nrRef ?? row.id}` }}
            />
          </TableCell>
          <TableCell
            padding="none"
            className={classes.stickyCell}
            style={{ left: 52, width: 40, minWidth: 40 }}
          >
            <Tooltip title="Historia zmian i komentarze">
              <IconButton size="small" onClick={handleExpandClick} aria-label="Pokaż historię">
                {loadingDetails ? (
                  <CircularProgress size={18} />
                ) : open ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.stickyCell} ${classes.stickyEdge}`}
            style={{ left: 92, width: "1%", whiteSpace: "nowrap" }}
          >
            <button type="button" className={classes.link} onClick={(e) => onOpen(row, e)}>
              {row.nrRef || "—"}
            </button>
          </TableCell>
          {columns.map((column) => (
            <TableCell key={column.id} style={{ minWidth: column.width, maxWidth: column.width }}>
              {renderCell(column.id)}
            </TableCell>
          ))}
          <TableCell style={{ width: 96, minWidth: 96 }}>
            <div className={classes.actions}>
              <Tooltip title="Szczegóły / edycja">
                <IconButton
                  size="small"
                  onClick={(e) => onOpen(row, e)}
                  onAuxClick={(e) => onOpen(row, e)}
                  aria-label="Szczegóły / edycja"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Usuń zgłoszenie">
                  <IconButton size="small" onClick={() => onDelete(row)} aria-label="Usuń zgłoszenie">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns.length + 4}
            className={`${classes.detailCell} ${open ? classes.detailCellOpen : ""}`}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Historia zmian</div>
                  <div className={classes.detailList}>
                    {editHistory.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak historii zmian dla tego zgłoszenia.</div>
                    ) : (
                      editHistory.map((change) => (
                        <div key={change.id} className={classes.detailItem}>
                          <strong>{change.fieldName || "-"}</strong>
                          <div className={classes.detailChange}>
                            <span style={{ color: COLORS.danger }}>{change.oldValue || "—"}</span>
                            {" → "}
                            <span style={{ color: COLORS.success, fontWeight: 600 }}>
                              {change.newValue || "—"}
                            </span>
                          </div>
                          <div className={classes.detailMeta}>
                            {change.editedBy || "-"} · {formatDateTime(change.editedAt)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Komentarze</div>
                  <div className={classes.detailList}>
                    {comments.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak komentarzy dla tego zgłoszenia.</div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className={classes.detailItem}>
                          <div>{comment.comment || "-"}</div>
                          <div className={classes.detailMeta}>
                            {comment.username || "-"} ·{" "}
                            {moment(comment.created_at).format("DD.MM.YYYY, HH:mm")}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
);

// Okno wyboru zakresu dat (wspolne dla raportow XLSX).
const DateRangeDialog = ({ open, title, onClose, onConfirm, start, end, onStart, onEnd, children }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <div style={{ display: "flex", flexDirection: "column", gap: 20, paddingTop: 8 }}>
        <TextField
          label="Data początkowa"
          type="date"
          value={start}
          onChange={(event) => onStart(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Data końcowa"
          type="date"
          value={end}
          onChange={(event) => onEnd(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {children}
      </div>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Anuluj</Button>
      <Button onClick={onConfirm} variant="contained" disableElevation>
        Generuj
      </Button>
    </DialogActions>
  </Dialog>
);

// -----------------------------------------------------------WIDOK--------------
const NowyHandling = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();
  const { username, isAdmin } = useSession();
  const hasFullReportAccess = FULL_REPORT_USERNAMES.includes(username);

  // Widoczne kolumny i rozmiar strony - w bazie (per uzytkownik), localStorage tylko jako cache.
  const prefs = useViewPreferences({
    view: VIEW,
    storageKey: COLUMNS_STORAGE_KEY,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: 50,
  });
  const rowsPerPage = prefs.pageSize;

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(readSavedFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const searchRef = useRef(null);

  // Raport Handlingowy XLSX - okno wyboru zakresu dat.
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [startReportDate, setStartReportDate] = useState("");
  const [endReportDate, setEndReportDate] = useState("");

  // Raport odprawa celna XLSX - okno wyboru zakresu dat i kierunku.
  const [showCustomsReportDialog, setShowCustomsReportDialog] = useState(false);
  const [startCustomsReportDate, setStartCustomsReportDate] = useState("");
  const [endCustomsReportDate, setEndCustomsReportDate] = useState("");
  const [selectedCustomsDirection, setSelectedCustomsDirection] = useState([]);

  // Raport Magazyn Warszawa (pelny) XLSX - okno wyboru zakresu dat utworzenia.
  const [showFullReportDialog, setShowFullReportDialog] = useState(false);
  const [startFullReportDate, setStartFullReportDate] = useState("");
  const [endFullReportDate, setEndFullReportDate] = useState("");

  // Zaznaczone wiersze: mapa { [id]: migawka danych wiersza }. Trzymana w
  // localStorage, dzieki czemu przetrwa odswiezenie strony i nawigacje.
  const [selectedById, setSelectedById] = useState(readSavedSelection);

  const debouncedFilters = useDebounced(filters, FILTER_DEBOUNCE_MS);

  const columns = useMemo(
    () => COLUMNS.filter((c) => !prefs.hiddenColumns.includes(c.id)),
    [prefs.hiddenColumns],
  );

  // --- persistencja filtrow (klucz wlasny, nie kolidujacy z Dashboard) -------
  useEffect(() => {
    const snapshot = {
      searchText: filters.search,
      nrRefFilter: filters.nrRef,
      dateFilter: filters.date,
      dataUtworzeniaOd: filters.from,
      dataUtworzeniaDo: filters.to,
      statusFilter: filters.status,
      createdByFilter: filters.createdBy,
      osobaPrzygotowujacaOdpraweFilter: filters.osobaPrzygotowujacaOdprawe,
      kierunekFilter: filters.kierunek,
      rewizjaFilter: filters.rewizja || "Wszystko",
      problemFilter: filters.problem || "Wszystko",
      towarCelnyFilter: filters.towarCelny || "Wszystko",
    };
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(snapshot));
      } catch (error) {
        console.error("Nie udało się zapisać filtrów:", error);
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [filters]);

  // --- persistencja zaznaczonych wierszy -----------------------------------
  useEffect(() => {
    try {
      localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(selectedById));
    } catch (error) {
      console.error("Nie udało się zapisać zaznaczenia:", error);
    }
  }, [selectedById]);

  // Po odswiezeniu danych: odswiez migawki zaznaczonych wierszy i usun te,
  // ktorych juz nie ma w zrodle (skasowane / inny rodzaj transportu).
  useEffect(() => {
    if (apiData.length === 0) return;
    setSelectedById((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      const next = {};
      let changed = false;
      ids.forEach((id) => {
        const fresh = apiData.find((r) => String(r.id) === String(id));
        if (fresh) {
          next[id] = fresh;
          if (fresh !== prev[id]) changed = true;
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [apiData]);

  // Skrot "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // --- pobieranie danych ----------------------------------------------------
  const createHeaders = () => {
    const token = localStorage.getItem("token") || "";
    if (!token) throw new Error("No token found in localStorage");
    return {
      Authorization: `Bearer ${token}`,
      "X-Use-New-Db": localStorage.getItem("useNewDb") || "false",
    };
  };

  const handleLogout = useCallback(() => {
    localStorage.clear();
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Use-New-Db"];
    toast.success("Wylogowano.", {
      autoClose: 500,
      onClose: () => navigate("/login"),
    });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}/userMwNowyHandling`, {
        headers: createHeaders(),
      });
      setApiData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Błąd pobierania danych:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          handleLogout();
          return;
        }
        const { data: userData } = await axios.get(`${BASE_URL}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        localStorage.setItem("useNewDb", userData.useNewDb ? "true" : "false");
        await fetchData();
      } catch (error) {
        console.error("❌ Błąd inicjalizacji:", error);
        if (mounted) handleLogout();
      }
    };

    initialize();
    return () => {
      mounted = false;
    };
  }, [fetchData, handleLogout]);

  // --- raport XLSX -----------------------------------------------------------
  const fetchAllComments = async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/comments`, {
        headers: createHeaders(),
      });
      return data || [];
    } catch (error) {
      console.error("❌ Błąd pobierania komentarzy:", error);
      return [];
    }
  };

  const handleGenerateReport = async () => {
    if (!startReportDate || !endReportDate) {
      toast.error("Proszę wybrać obie daty");
      return;
    }

    const loadingToast = toast.info("Generowanie raportu...", {
      autoClose: false,
    });

    try {
      const allComments = await fetchAllComments();

      const start = new Date(startReportDate + "T00:00:00");
      const end = new Date(endReportDate + "T23:59:59");

      const filteredData = apiData.filter((item) => {
        const itemDate = new Date(item.utworzono);
        const inRange = itemDate >= start && itemDate <= end;
        return inRange && !isYes(item.towarCelny);
      });

      const reportData = filteredData.map((item) => {
        const itemComments = allComments.filter(
          (comment) => comment.customId === item.id,
        );
        const commentsText =
          itemComments.length > 0
            ? itemComments
                .map(
                  (comment) =>
                    `${comment.comment} (${moment(comment.created_at).format(
                      "DD.MM.YYYY HH:mm",
                    )})`,
                )
                .join(" | ")
            : "";

        return {
          "Nr. Zlecenia/nrRef": item.nrRef || "",
          "Data utworzenia": moment(item.utworzono).format("DD.MM.YYYY"),
          "Numer listu przewozowego": item.numerListu || "",
          "Nazwa firmy/nadawca": item.nadawca || "",
          Paczki: item.iloscFaktPaczek || "",
          Palety: item.iloscFaktPalet || "",
          "Koszt handlingu": "",
          MRN: "",
          "Ilość pozycji SAD": "",
          "Koszt odprawy": "",
          Rewizja: item.rewizja || "",
          Kierunek: item.kierunek || "",
          "Osoba tworząca": item.createdBy || "",
          Komentarze: commentsText,
          "Ilość dekl. Paczki": item.iloscDeklPaczek || "",
          "Ilość dekl. Palety": item.iloscDeklPalet || "",
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(reportData);
      XLSX.utils.book_append_sheet(wb, ws, "Raport_Handlingowy");

      const fileName = `Raport_Handlingowy_${moment(startReportDate).format(
        "DD_MM_YYYY",
      )}_${moment(endReportDate).format("DD_MM_YYYY")}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.dismiss(loadingToast);
      toast.success("Raport został wygenerowany pomyślnie!");

      setShowReportDialog(false);
    } catch (error) {
      console.error("❌ Błąd generowania raportu:", error);
      toast.dismiss(loadingToast);
      toast.error("Błąd podczas generowania raportu");
    }
  };

  const handleGenerateCustomsReport = async () => {
    if (!startCustomsReportDate || !endCustomsReportDate) {
      toast.error("Proszę wybrać obie daty");
      return;
    }

    const loadingToast = toast.info("Generowanie raportu...", {
      autoClose: false,
    });

    try {
      const allComments = await fetchAllComments();

      const start = new Date(startCustomsReportDate + "T00:00:00");
      const end = new Date(endCustomsReportDate + "T23:59:59");

      let filteredData = apiData.filter((item) => {
        const itemDate = new Date(item.utworzono);
        const inRange = itemDate >= start && itemDate <= end;
        return inRange && isYes(item.towarCelny);
      });

      if (selectedCustomsDirection.length > 0) {
        filteredData = filteredData.filter((item) =>
          selectedCustomsDirection.includes(item.kierunek),
        );
      }

      const reportData = filteredData.map((item) => {
        const itemComments = allComments.filter(
          (comment) => comment.customId === item.id,
        );
        const commentsText =
          itemComments.length > 0
            ? itemComments
                .map(
                  (comment) =>
                    `${comment.comment} (${moment(comment.created_at).format(
                      "DD.MM.YYYY HH:mm",
                    )})`,
                )
                .join(" | ")
            : "";

        return {
          "Nr. Zlecenia/nrRef": item.nrRef || "",
          "Data zakończenia": item.endDate
            ? moment(item.endDate).format("DD.MM.YYYY")
            : "",
          "Numer listu przewozowego": item.numerListu || "",
          "Nazwa firmy/nadawca": item.nadawca || "",
          Paczki: item.iloscFaktPaczek || "",
          Palety: item.iloscDeklPalet || "",
          "Koszt handlingu": "",
          MRN: item.MRN || "",
          "Ilość pozycji SAD": item.iloscPozycjiSAD || "",
          "Koszt odprawy": "",
          Rewizja: item.rewizja || "",
          Kierunek: item.kierunek || "",
          "Serwis lotniczy": item.serwisLotniczy || "",
          "Osoba tworząca zgłoszenie": item.createdBy || "",
          Komentarze: commentsText,
          "Ilość dekl. paczka": item.iloscDeklPaczek || "",
          "Ilość dekl. palety": item.iloscDeklPalet || "",
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(reportData);
      XLSX.utils.book_append_sheet(wb, ws, "Raport_Odprawa_Celna");

      const directionSuffix =
        selectedCustomsDirection.length > 0
          ? `_${selectedCustomsDirection.join("_")}`
          : "";
      const fileName = `Raport_Odprawa_Celna_${moment(
        startCustomsReportDate,
      ).format("DD_MM_YYYY")}_${moment(endCustomsReportDate).format(
        "DD_MM_YYYY",
      )}${directionSuffix}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.dismiss(loadingToast);
      toast.success("Raport został wygenerowany pomyślnie!");

      setShowCustomsReportDialog(false);
      setSelectedCustomsDirection([]);
    } catch (error) {
      console.error("❌ Błąd generowania raportu:", error);
      toast.dismiss(loadingToast);
      toast.error("Błąd podczas generowania raportu");
    }
  };

  const handleGenerateFullReport = () => {
    if (!startFullReportDate || !endFullReportDate) {
      toast.error("Proszę wybrać obie daty");
      return;
    }
    if (startFullReportDate > endFullReportDate) {
      toast.error("Data początkowa nie może być późniejsza niż końcowa");
      return;
    }

    try {
      const start = moment.tz(startFullReportDate, "Europe/Warsaw").startOf("day");
      const end = moment.tz(endFullReportDate, "Europe/Warsaw").endOf("day");

      const reportData = apiData
        .filter((item) => {
          const created = moment.tz(item.utworzono, "Europe/Warsaw");
          return created.isValid() && created.isBetween(start, end, null, "[]");
        })
        .sort((a, b) => new Date(a.utworzono) - new Date(b.utworzono))
        .map((item) => ({
          "Nr. Zlecenia/nrRef": item.nrRef || "",
          "Data i godzina utworzenia": moment
            .tz(item.utworzono, "Europe/Warsaw")
            .format("DD.MM.YYYY HH:mm"),
          "Przyjazd auta": withCreatedDate(item.przyjazdAuta, item.utworzono),
          "Start skanowania": withCreatedDate(
            item.startSkanowania,
            item.utworzono,
          ),
          "Zakończenie skanowania": withCreatedDate(
            item.zakonczenieSkanowania,
            item.utworzono,
          ),
          "Towar celny": item.towarCelny || "",
          "Numer listu przewozowego": item.numerListu || "",
          "Nazwa firmy/nadawca": item.nadawca || "",
          Paczki: "",
          Palety: "",
          "Koszt handlingu": "",
          MRN: item.MRN || "",
          "Ilość pozycji SAD": item.iloscPozycjiSAD || "",
          "Koszt odprawy": "",
          Rewizja: item.rewizja || "",
          Kierunek: item.kierunek || "",
          "Osoba tworząca": item.createdBy || "",
          "Serwis lotniczy": item.serwisLotniczy || "",
          "Ilość dekl. Paczki": item.iloscDeklPaczek || "",
          "Ilość dekl. Palety": item.iloscDeklPalet || "",
          "Ilość fakt. paczek": item.iloscFaktPaczek || "",
          "Ilość fakt. Palet": item.iloscFaktPalet || "",
          "Problem z przesyłką": item.problemZPrzesylka || "",
          "Podjazd auta": withCreatedDate(item.podjazdAuta, item.utworzono),
          "Godzina wyjazdu auta": withCreatedDate(
            item.wyjazdAuta,
            item.utworzono,
          ),
        }));

      if (reportData.length === 0) {
        toast.info("Brak danych w wybranym zakresie dat");
        return;
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(reportData);
      XLSX.utils.book_append_sheet(wb, ws, "Raport_Magazyn_Warszawa");

      const fileName = `Raport_Magazyn_Warszawa_${moment(
        startFullReportDate,
      ).format("DD_MM_YYYY")}_${moment(endFullReportDate).format(
        "DD_MM_YYYY",
      )}.xlsx`;

      XLSX.writeFile(wb, fileName);

      toast.success("Raport został wygenerowany pomyślnie!");
      setShowFullReportDialog(false);
    } catch (error) {
      console.error("❌ Błąd generowania raportu:", error);
      toast.error("Błąd podczas generowania raportu");
    }
  };


  // --- opcje filtrow wyliczane z danych ------------------------------------
  const createdByOptions = useMemo(() => sortPl(apiData.map((r) => r.createdBy)), [apiData]);
  const osobaPrzygotowujacaOdpraweOptions = useMemo(
    () => sortPl(apiData.map((r) => r.osobaPrzygotowujacaOdprawe)),
    [apiData],
  );
  const kierunekOptions = useMemo(() => sortPl(apiData.map((r) => r.kierunek)), [apiData]);

  // --- filtrowanie na zywo ------------------------------------------------------
  const filteredData = useMemo(() => {
    const f = debouncedFilters;
    let rows = apiData;

    if (f.status.length > 0) {
      rows = rows.filter((row) => f.status.includes(row.statusRealizacji));
    }
    if (f.createdBy.length > 0) {
      rows = rows.filter((row) => f.createdBy.includes(row.createdBy));
    }
    if (f.osobaPrzygotowujacaOdprawe.length > 0) {
      rows = rows.filter((row) =>
        f.osobaPrzygotowujacaOdprawe.includes(row.osobaPrzygotowujacaOdprawe),
      );
    }
    if (f.kierunek.length > 0) {
      rows = rows.filter((row) => f.kierunek.includes(row.kierunek));
    }
    if (f.rewizja) rows = rows.filter((row) => matchesYesNo(row.rewizja, f.rewizja));
    if (f.problem) {
      rows = rows.filter((row) => matchesYesNo(row.problemZPrzesylka, f.problem));
    }
    if (f.towarCelny) {
      rows = rows.filter((row) => matchesYesNo(row.towarCelny, f.towarCelny));
    }

    const nrRefTokens = parseMultiSearchValues(f.nrRef);
    if (nrRefTokens.length > 0) {
      rows = rows.filter((row) => {
        const value = (row.nrRef || "").toString().toLowerCase();
        return nrRefTokens.some((token) => value.includes(token));
      });
    }

    if (f.date) {
      rows = rows.filter((row) =>
        moment.utc(row.utworzono).format("DD-MM-YYYY").includes(f.date),
      );
    }

    if (f.from && f.to) {
      const start = new Date(`${f.from}T00:00:00`);
      const end = new Date(`${f.to}T23:59:59.999`);
      rows = rows.filter((row) => {
        const created = new Date(row.utworzono);
        return created >= start && created <= end;
      });
    }

    const query = f.search.trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) =>
        Object.values(row)
          .map((value) => (value == null ? "" : value.toString()))
          .join(" ")
          .toLowerCase()
          .includes(query),
      );
    }

    return rows;
  }, [apiData, debouncedFilters]);

  useEffect(() => {
    setPage(0);
  }, [filteredData]);

  const pageRows = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage],
  );

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const clearAllFilters = () => setFilters(EMPTY_FILTERS);

  const activeChips = useMemo(() => describeFilters(filters), [filters]);
  const activeCount = useMemo(
    () => describeFilters({ ...filters, search: "" }).length,
    [filters],
  );
  const hasFilters = activeChips.length > 0;

  // --- zaznaczanie wielu wierszy -----------------------------------------------
  const selectedCount = useMemo(() => Object.keys(selectedById).length, [selectedById]);

  const toggleRowSelected = useCallback((row) => {
    setSelectedById((prev) => {
      const next = { ...prev };
      if (next[row.id]) {
        delete next[row.id];
      } else {
        next[row.id] = row;
      }
      return next;
    });
  }, []);

  const pageAllSelected = pageRows.length > 0 && pageRows.every((row) => selectedById[row.id]);
  const pageSomeSelected = !pageAllSelected && pageRows.some((row) => selectedById[row.id]);

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedById((prev) => {
      const next = { ...prev };
      const allSelected = pageRows.length > 0 && pageRows.every((row) => next[row.id]);
      if (allSelected) {
        pageRows.forEach((row) => delete next[row.id]);
      } else {
        pageRows.forEach((row) => {
          next[row.id] = row;
        });
      }
      return next;
    });
  }, [pageRows]);

  const handleClearSelection = () => setSelectedById({});

  const handleDopiszDane = () => {
    if (selectedCount === 0) return;
    const rows = Object.values(selectedById);
    // Migawka odczytywana przez widok "Dopisz dane".
    try {
      localStorage.setItem(DOPISZ_DANE_STORAGE_KEY, JSON.stringify(rows));
    } catch (error) {
      console.error("Nie udało się zapisać danych do dopisania:", error);
    }
    navigate("/magazynWarszawaNowyHandling/dopisz-dane");
  };

  const handleOpenDetail = useCallback(
    (row, event) => {
      const url = `/detail/${encodeURIComponent(row.nrRef)}/${encodeURIComponent(row.id)}`;
      if (event?.button === 1 || event?.ctrlKey || event?.metaKey) {
        window.open(url, "_blank");
      } else {
        navigate(url, { state: { from: "magazynWarszawaNowyHandling" } });
      }
    },
    [navigate],
  );

  const handleConfirmDelete = async () => {
    const row = deleteTarget;
    if (!row || !isAdmin) return;
    try {
      await axios.delete(`${BASE_URL}/delete/${row.id}`, { headers: authHeaders() });
      toast.success("Poprawnie usunięto zgłoszenie!", { autoClose: 3000 });
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting row:", error);
      toast.error("Wystąpił błąd podczas usuwania.", { autoClose: 3000 });
    }
  };

  const presets = [
    { id: "rewizja", label: "Rewizja", hint: "Zgłoszenia z rewizją", key: "rewizja", value: "Tak" },
    {
      id: "problem",
      label: "Problem z przesyłką",
      hint: "Zgłoszenia z problemem z przesyłką",
      key: "problem",
      value: "Tak",
    },
    {
      id: "towarCelny",
      label: "Towar celny",
      hint: "Tylko towar celny (odprawa celna)",
      key: "towarCelny",
      value: "Tak",
    },
  ];

  // ---- Pola panelu filtrow ----
  const text = (key, label, extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      autoComplete="off"
      value={filters[key]}
      onChange={(e) => updateFilters({ [key]: e.target.value })}
      {...extra}
    />
  );

  const multi = (key, label, options) => (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={filters[key]}
      onChange={(_, value) => updateFilters({ [key]: value })}
      noOptionsText="Brak opcji"
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );

  const yesNo = (key, label) => (
    <TextField
      select
      label={label}
      size="small"
      fullWidth
      value={filters[key]}
      onChange={(e) => updateFilters({ [key]: e.target.value })}
    >
      <MenuItem value="" style={{ fontWeight: "bold" }}>
        Wszystko
      </MenuItem>
      {YES_NO_VALUES.map((item) => (
        <MenuItem key={item} value={item}>
          {item}
        </MenuItem>
      ))}
    </TextField>
  );

  const headerSticky = [
    { left: 0, width: 52 },
    { left: 52, width: 40 },
    { left: 92 },
  ];

  const closeReportsAnd = (open) => () => {
    setReportsAnchor(null);
    open(true);
  };

  const closeCustomsDialog = () => {
    setShowCustomsReportDialog(false);
    setSelectedCustomsDirection([]);
  };

  return (
    <>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>Magazyn Warszawa</h1>
          <div className={classes.subtitle}>
            <span>
              <strong style={{ color: "#212121" }}>{filteredData.length}</strong>{" "}
              {hasFilters ? "zgłoszeń spełnia filtry" : "aktywnych zgłoszeń"}
            </span>
            <Tooltip title="Odśwież teraz">
              <IconButton size="small" onClick={fetchData} aria-label="Odśwież">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            Kolumny
          </Button>
          <Button
            variant="outlined"
            startIcon={<SummarizeIcon />}
            onClick={(e) => setReportsAnchor(e.currentTarget)}
          >
            Raporty
          </Button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              inputRef={searchRef}
              className={classes.searchInput}
              placeholder="Szukaj po nr ref, nr listu, nazwie firmy…"
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              inputProps={{ "aria-label": "Szukaj" }}
            />
            {filters.search ? (
              <IconButton
                size="small"
                onClick={() => updateFilters({ search: "" })}
                aria-label="Wyczyść wyszukiwanie"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <kbd className={classes.kbd}>/</kbd>
            )}
          </div>

          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              disableElevation
            >
              Filtry
            </Button>
          </Badge>

          {presets.map((preset) => {
            const active = filters[preset.key] === preset.value;
            const patch = { [preset.key]: active ? "" : preset.value };
            return (
              <Tooltip key={preset.id} title={preset.hint}>
                <Chip
                  className={classes.presetChip}
                  label={preset.label}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  onClick={() => updateFilters(patch)}
                  onDelete={active ? () => updateFilters(patch) : undefined}
                />
              </Tooltip>
            );
          })}

          <span className={classes.toolbarGap} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters}>
              Wyczyść filtry
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className={classes.activeFilters}>
            {activeChips.map((chip) => (
              <Chip
                key={chip.id}
                size="small"
                label={chip.label}
                onDelete={() => updateFilters(chip.clear)}
                style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
              />
            ))}
          </div>
        )}

        {selectedCount > 0 && (
          <div className={classes.selectionBar}>
            <strong>Zaznaczono: {selectedCount}</strong>
            <Button
              size="small"
              variant="contained"
              startIcon={<EditNoteIcon />}
              onClick={handleDopiszDane}
              disableElevation
            >
              Dopisz dane
            </Button>
            <Button size="small" onClick={handleClearSelection}>
              Wyczyść zaznaczenie
            </Button>
          </div>
        )}

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table
            stickyHeader
            size="small"
            style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{
                    left: headerSticky[0].left,
                    width: headerSticky[0].width,
                    minWidth: headerSticky[0].width,
                  }}
                >
                  <Tooltip arrow title="Zaznacz / odznacz wszystkie na stronie">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={pageAllSelected}
                      indeterminate={pageSomeSelected}
                      onChange={toggleSelectAllOnPage}
                      inputProps={{ "aria-label": "Zaznacz wszystkie na stronie" }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell
                  padding="none"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{
                    left: headerSticky[1].left,
                    width: headerSticky[1].width,
                    minWidth: headerSticky[1].width,
                  }}
                />
                <TableCell
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{
                    left: headerSticky[2].left,
                    width: "1%",
                    whiteSpace: "nowrap",
                    boxShadow: "inset -1px 0 0 #e0e0e0",
                  }}
                >
                  Nr Ref
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={classes.headCell}
                    style={{ minWidth: column.width, maxWidth: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell className={classes.headCell} style={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  columns={columns}
                  isSelected={Boolean(selectedById[row.id])}
                  onToggleSelect={toggleRowSelected}
                  onOpen={handleOpenDetail}
                  onDelete={setDeleteTarget}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>

          {!loading && filteredData.length === 0 && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>
                Brak zgłoszeń do wyświetlenia
              </strong>
              <span>
                {hasFilters
                  ? "Żadne zgłoszenie nie spełnia wybranych filtrów."
                  : "Brak aktywnych zgłoszeń w Magazynie Warszawa."}
              </span>
              {hasFilters && (
                <Button variant="outlined" onClick={clearAllFilters}>
                  Wyczyść filtry
                </Button>
              )}
            </div>
          )}
        </div>

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            prefs.setPageSize(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </div>

      <div className={classes.legend}>
        {ROW_LEGEND.map((item) => (
          <span key={item.key} className={classes.legendItem}>
            <span
              className={classes.legendTone}
              style={{ background: item.background, boxShadow: `inset 3px 0 0 ${item.stripe}` }}
            />
            {item.label}
          </span>
        ))}
      </div>

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onClear={clearAllFilters}
        activeCount={activeCount}
        total={filteredData.length}
        totalLabel="Zgłoszeń"
      >
        <FilterCard
          title="Wyszukiwanie"
          count={countFilled(filters.nrRef, filters.date, filters.from || filters.to)}
        >
          {text("nrRef", "Nr Ref", {
            multiline: true,
            maxRows: 4,
            helperText: "Wiele numerów po przecinku lub w nowych liniach",
          })}
          {text("date", "Data utworzenia (dd-mm-rrrr)")}
          {text("from", "Utworzono od", {
            type: "date",
            InputLabelProps: { shrink: true },
          })}
          {text("to", "Utworzono do", {
            type: "date",
            InputLabelProps: { shrink: true },
          })}
        </FilterCard>
        <FilterCard
          title="Status i flagi"
          count={countFilled(
            filters.status,
            filters.rewizja,
            filters.problem,
            filters.towarCelny,
            filters.kierunek,
          )}
        >
          {multi("status", "Status", STATUS_VALUES)}
          {multi("kierunek", "Kierunek", kierunekOptions)}
          {yesNo("rewizja", "Rewizja")}
          {yesNo("problem", "Problem z przesyłką")}
          {yesNo("towarCelny", "Towar celny")}
        </FilterCard>
        <FilterCard
          title="Osoby"
          count={countFilled(filters.createdBy, filters.osobaPrzygotowujacaOdprawe)}
        >
          {multi("createdBy", "Utworzono przez", createdByOptions)}
          {multi(
            "osobaPrzygotowujacaOdprawe",
            "Osoba przygotowująca odprawę",
            osobaPrzygotowujacaOdpraweOptions,
          )}
        </FilterCard>
      </FilterDrawer>

      <ColumnsMenu
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        columns={COLUMNS}
        hiddenColumns={prefs.hiddenColumns}
        onToggle={prefs.toggleColumn}
        onReset={prefs.resetColumns}
      />

      <Menu
        anchorEl={reportsAnchor}
        open={Boolean(reportsAnchor)}
        onClose={() => setReportsAnchor(null)}
      >
        <MenuItem onClick={closeReportsAnd(setShowReportDialog)}>
          <ListItemText primary="Raport Handlingowy" />
        </MenuItem>
        <MenuItem onClick={closeReportsAnd(setShowCustomsReportDialog)}>
          <ListItemText primary="Raport odprawa celna" />
        </MenuItem>
        {hasFullReportAccess && (
          <MenuItem onClick={closeReportsAnd(setShowFullReportDialog)}>
            <ListItemText primary="Eksport danych" />
          </MenuItem>
        )}
      </Menu>

      <DateRangeDialog
        open={showFullReportDialog}
        title="Eksport danych - zakres dat utworzenia"
        onClose={() => setShowFullReportDialog(false)}
        onConfirm={handleGenerateFullReport}
        start={startFullReportDate}
        end={endFullReportDate}
        onStart={setStartFullReportDate}
        onEnd={setEndFullReportDate}
      />

      <DateRangeDialog
        open={showReportDialog}
        title="Raport Handlingowy - zakres dat"
        onClose={() => setShowReportDialog(false)}
        onConfirm={handleGenerateReport}
        start={startReportDate}
        end={endReportDate}
        onStart={setStartReportDate}
        onEnd={setEndReportDate}
      />

      <DateRangeDialog
        open={showCustomsReportDialog}
        title="Raport odprawa celna - zakres dat i kierunek"
        onClose={closeCustomsDialog}
        onConfirm={handleGenerateCustomsReport}
        start={startCustomsReportDate}
        end={endCustomsReportDate}
        onStart={setStartCustomsReportDate}
        onEnd={setEndCustomsReportDate}
      >
        <Autocomplete
          multiple
          options={kierunekOptions}
          value={selectedCustomsDirection}
          onChange={(_, value) => setSelectedCustomsDirection(value)}
          noOptionsText="Brak opcji"
          renderInput={(params) => (
            <TextField {...params} label="Kierunek (opcjonalnie)" />
          )}
        />
      </DateRangeDialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Usunąć zgłoszenie?</DialogTitle>
        <DialogContent>
          Zgłoszenie <strong>{deleteTarget?.nrRef}</strong> zostanie trwale usunięte.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NowyHandling;
